import * as React from 'react';
import './InputFloating.scss';

type InputProps = Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'value' | 'onChange'
> & {
  as?: 'input';
  type?: string;
  id: string;
  label: string;
  min?: number | string;
  max?: number | string;
  value?: string | number;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  icon?: string | React.ComponentType<any>;
};

type TextareaProps = Omit<
  React.TextareaHTMLAttributes<HTMLTextAreaElement>,
  'value' | 'onChange'
> & {
  as: 'textarea';
  id: string;
  label: string;
  rows?: number;
  value?: string;
  onChange?: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
};

type CommonProps = {
  wrapperClass?: string;
  inputClass?: string;
  labelClass?: string;
};

type FloatingInputProps = (InputProps | TextareaProps) & CommonProps;

const useIsMobile = () => {
  const [isMobile, setIsMobile] = React.useState(false);

  React.useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.matchMedia('(pointer: coarse)').matches);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  return isMobile;
};


const useIsIOS = () => {
  const [isIOS, setIsIOS] = React.useState(false);

  React.useEffect(() => {
    const checkIOS = () => {
      setIsIOS(/iPad|iPhone|iPod/.test(navigator.userAgent));
    };
    checkIOS();
    window.addEventListener('resize', checkIOS);
    return () => window.removeEventListener('resize', checkIOS);
  }, []);

  return isIOS;
};

export const FloatingInput = React.forwardRef<
  HTMLInputElement | HTMLTextAreaElement,
  FloatingInputProps
>((props, ref) => {
  const {
    id,
    label,
    wrapperClass = '',
    inputClass = '',
    labelClass = '',
    value,
    ...rest
  } = props;

  const baseInputClass = `floating-input__field ${inputClass}`;
  const baseLabelClass = `floating-input__label ${labelClass}`;
  const baseWrapperClass = `floating-input ${wrapperClass}`;

  const isMobile = useIsMobile();

  const isIOS = useIsIOS();

  if ('as' in props && props.as === 'textarea') {
    const {
      onChange,
      rows = 3,
      id: _,
      ...textareaRest
    } = rest as TextareaProps;
    const hasValue = Boolean(value && value.toString().trim().length > 0);

    return (
      <div className={`${baseWrapperClass} ${hasValue ? 'has-value' : ''}`}>
        <textarea
          id={id}
          ref={ref as React.Ref<HTMLTextAreaElement>}
          placeholder=" "
          rows={rows}
          className={baseInputClass}
          value={value ?? ''}
          onChange={onChange}
          {...textareaRest}
        />
        <label htmlFor={id} className={baseLabelClass}>
          {label}
        </label>
      </div>
    );
  }

  const {
    onChange,
    type = 'text',
    min,
    max,
    id: _,
    icon,
    ...inputRest
  } = rest as InputProps;

  const handleNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    // Allow empty string, integers, and decimal numbers with max 2 decimal places
    if (newValue === '' || /^[0-9]*\.?[0-9]{0,2}$/.test(newValue)) {
      // Always allow typing, but only call onChange for valid values
      if (min !== undefined && max !== undefined && newValue !== '') {
        const numValue = Number(newValue);
        const minValue = Number(min);
        
        // Allow intermediate values (like "0", "0.0") for typing
        if (numValue === 0 || numValue >= minValue && numValue <= Number(max)) {
          onChange?.(e);
        }
      } else {
        onChange?.(e);
      }
    }
  };

  // iOS Safari focus fix for number inputs
  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    if (isIOS && type === 'number') {
      // Small delay to ensure proper focus on iOS
      setTimeout(() => {
        e.target.focus();
      }, 0);
    }
  };

  // iOS Safari click fix for number inputs
  const handleClick = (e: React.MouseEvent<HTMLInputElement>) => {
    if (isIOS && type === 'number') {
      // Ensure focus on click for iOS
      setTimeout(() => {
        e.currentTarget.focus();
      }, 10);
    }
  };

  return (
    <div className={baseWrapperClass}>
      <input
        id={id}
        ref={ref as React.Ref<HTMLInputElement>}
        type={type === 'number' ? 'text' : type}
        placeholder=" "
        className={baseInputClass}
        value={value ?? ''}
        onChange={type === 'number' ? handleNumberChange : onChange}
        onFocus={type === 'number' ? handleFocus : undefined}
        onClick={type === 'number' ? handleClick : undefined}
        inputMode={
          type === 'number'
            ? (isIOS ? 'number' as 'decimal' : 'numeric')
            : type === 'email' && isIOS
              ? 'email'
              : undefined
        }
        // pattern={type === 'number' ? '^[0-9]{1,4}(\.[0-9]{1,2})?$' : undefined}
        min={min}
        max={max}
        {...inputRest}
      />
      <label htmlFor={id} className={baseLabelClass}>
        {label}
      </label>
      {icon && (
        typeof icon === 'string' ? (
          <img 
            src={icon} 
            alt="icon" 
            className="absolute right-3 top-1/2 transform -translate-y-1/2 w-8 h-8"
            style={{ filter: 'brightness(0) saturate(100%) invert(78%) sepia(8%) saturate(1000%) hue-rotate(25deg) brightness(95%) contrast(88%)' }}
          />
        ) : (
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2 w-8 h-8 flex items-center justify-center">
            {React.createElement(icon, { 
              size: 32,
              className: 'w-8 h-8'
            })}
          </div>
        )
      )}
    </div>
  );
});

FloatingInput.displayName = 'FloatingInput';
